import React, { useEffect, useRef, useState } from "react";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./SettingsPage.module.scss";

const RESEND_SECONDS = 60;

const PasswordCard = ({ email, user, remindPassword }) => {
  const [phase, setPhase] = useState("idle"); // idle | sent | failed
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(null);

  const sending = user.remindPassword;

  useEffect(() => {
    if (user.remindPasswordSuccess) {
      setPhase("sent");
      setCountdown(RESEND_SECONDS);
    }
  }, [user.remindPasswordSuccess]);

  useEffect(() => {
    if (user.remindPasswordError) {
      setPhase("failed");
    }
  }, [user.remindPasswordError]);

  const canResend = phase !== "sent" || countdown <= 0;

  useEffect(() => {
    if (canResend) {
      clearInterval(intervalRef.current);
      return undefined;
    }
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [canResend]);

  const onSend = () => {
    remindPassword(email);
  };

  const countdownLabel = `0:${String(countdown).padStart(2, "0")}`;

  return (
    <Card aria-labelledby="password-title">
      <Card.Header
        id="password-title"
        title="Password"
        subtitle="We email a reset link rather than asking for your current password."
      />
      <Card.Body>
        <div className={styles.row}>
          {phase === "sent" ? (
            <span className={`${styles.footerMessage} ${styles["footerMessage--success"]}`}>
              <Icon
                name="CheckCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--success"]}`}
              />
              <span>
                <strong>Reset link sent to {email}.</strong> It expires in 60 minutes. Check
                your spam folder if it hasn't arrived.
              </span>
            </span>
          ) : phase === "failed" ? (
            <span className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}>
              <Icon
                name="AlertCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
              />
              <span>
                <strong>Couldn't send the link.</strong> The service is unavailable — try again
                in a moment.
              </span>
            </span>
          ) : (
            <p>
              The link goes to <strong>{email}</strong> and expires after 60 minutes.
            </p>
          )}

          <div className={styles.rowActions}>
            <Button
              hierarchy="secondaryGray"
              onClick={onSend}
              disabled={sending || !canResend}
              loading={sending}
            >
              {sending
                ? "Sending…"
                : phase === "sent" && !canResend
                ? `Resend in ${countdownLabel}`
                : phase === "failed"
                ? "Try again"
                : phase === "sent"
                ? "Resend link"
                : "Send reset link"}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default PasswordCard;
